/**
 * Standalone form dialog for creating or editing an ingestion rule.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { AddLineButton, DialogForm } from '../../../shared/components/common';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DOCUMENT_TYPE_VALUES } from '../../../generated/config-enums';
import { useNotification } from '../../../shared/context/NotificationContext';
import { useCreateIngestionRule, useUpdateIngestionRule } from '../hooks/useIngestion';
import type {
  IngestionRule,
  IngestionRuleCondition,
  IngestionRuleConditionField,
  IngestionRuleConditionOperator,
  IngestionRuleActions,
  StoredIngestionRuleActions,
  // Re-exported from the generated config binding. Imported by NAME on purpose:
  // the DOM lib declares a global `DocumentType` interface, so dropping this
  // import does not fail — the cast below silently resolves to the DOM node type.
  DocumentType,
  JeDetailLevel,
} from '../services/accountingApi';

/**
 * Drop the retired `set_company_id` action from a stored rule before it reaches
 * the form state. Two things depend on this: the submit path sends `actions`
 * verbatim, and the API now REJECTS that key — so without the strip, editing any
 * pre-Phase-5a rule that carries it fails validation. Stripping also means the
 * next save cleans the dead key out of the row.
 */
function stripRetiredActions(stored: IngestionRuleActions): IngestionRuleActions {
  const { set_company_id: _retired, ...live } = stored as StoredIngestionRuleActions;
  return live;
}

const CONDITION_FIELDS: IngestionRuleConditionField[] = [
  'payment_card_last4',
  'payment_method',
  'vendor_name',
  'description',
  'amount',
  'currency',
  'document_type',
];

const OPERATORS_BY_FIELD: Record<string, IngestionRuleConditionOperator[]> = {
  payment_card_last4: ['equals'],
  payment_method: ['contains', 'equals'],
  vendor_name: ['contains', 'equals'],
  description: ['contains', 'equals'],
  amount: ['equals', 'greater_than', 'less_than'],
  currency: ['equals'],
  document_type: ['equals'],
};

const JE_DETAIL_LEVELS: JeDetailLevel[] = ['summary', 'grouped', 'detailed'];

interface IngestionRuleFormDialogProps {
  open: boolean;
  onClose: () => void;
  rule?: IngestionRule | null;
}

export function IngestionRuleFormDialog({
  open,
  onClose,
  rule,
}: IngestionRuleFormDialogProps) {
  const { t } = useTranslation('accounting');
  const { showSuccess } = useNotification();
  const isEditing = !!rule;

  const createRule = useCreateIngestionRule();
  const updateRule = useUpdateIngestionRule();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [conditions, setConditions] = useState<IngestionRuleCondition[]>([]);
  const [actions, setActions] = useState<IngestionRuleActions>({});
  // True when the rule being edited still carries the retired `set_company_id`
  // action (see `stripRetiredActions`), so the dialog can say why it now has one
  // fewer action than the user configured.
  const [hasRetiredAction, setHasRetiredAction] = useState(false);
  const [priority, setPriority] = useState(100);
  const [autoApprove, setAutoApprove] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      if (rule) {
        setName(rule.name);
        setDescription(rule.description ?? '');
        setConditions(rule.conditions);
        setActions(stripRetiredActions(rule.actions));
        setHasRetiredAction(
          !!(rule.actions as StoredIngestionRuleActions).set_company_id,
        );
        setPriority(rule.priority);
        setAutoApprove(rule.auto_approve);
        setIsActive(rule.is_active);
      } else {
        setName('');
        setDescription('');
        setConditions([]);
        setActions({});
        setHasRetiredAction(false);
        setPriority(100);
        setAutoApprove(false);
        setIsActive(true);
      }
    }
  }, [open, rule]);

  const updateCondition = (index: number, key: keyof IngestionRuleCondition, value: string | number) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [key]: value };
    if (key === 'field') {
      const ops = OPERATORS_BY_FIELD[value as string] || ['equals'];
      updated[index].operator = ops[0];
    }
    setConditions(updated);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const addCondition = () => {
    setConditions([...conditions, { field: 'vendor_name', operator: 'equals', value: '' }]);
  };

  const hasTargetAction =
    !!actions.set_payment_account_id
    || !!actions.set_account_id
    || !!actions.set_tax_rate_id
    || !!actions.set_document_type
    || !!actions.set_je_detail_level;

  const handleSubmit = async () => {
    if (!name.trim() || conditions.length === 0 || !hasTargetAction) return;

    try {
      if (isEditing && rule) {
        await updateRule.mutateAsync({
          ruleId: rule.id,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
            conditions,
            actions,
            priority,
            auto_approve: autoApprove,
            is_active: isActive,
          },
        });
        showSuccess(t('documentImport.rules.updateSuccess'));
      } else {
        await createRule.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          conditions,
          actions,
          priority,
          auto_approve: autoApprove,
        });
        showSuccess(t('documentImport.rules.createSuccess'));
      }
      onClose();
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const isSubmitDisabled = !name.trim() || conditions.length === 0 || !hasTargetAction;

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEditing ? t('documentImport.rules.editTitle') : t('documentImport.rules.createTitle')}
      submitLabel={isEditing ? t('common:app.save') : t('common:app.create')}
      isLoading={createRule.isPending || updateRule.isPending}
      isDisabled={isSubmitDisabled}
      maxWidth="xl"
    >
      <div className="space-y-5">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="rule-name" required>{t('common:app.name')}</Label>
          <Input
            id="rule-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('documentImport.rules.namePlaceholder')}
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="rule-description">{t('common:app.description')}</Label>
          <Textarea
            id="rule-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('documentImport.rules.descriptionPlaceholder')}
            rows={2}
          />
        </div>

        {/* Conditions */}
        <div className="space-y-2">
          <Label>{t('documentImport.rules.conditions')}</Label>
          <p className="text-xs text-muted-foreground">
            {t('documentImport.rules.conditionsHint')}
          </p>
          {conditions.length > 0 && (
            <div className="space-y-2">
              {conditions.map((condition, i) => (
                <div key={`${condition.field}-${i}`} className="flex items-center gap-2">
                  <Select
                    value={condition.field}
                    onValueChange={(v) => updateCondition(i, 'field', v)}
                  >
                    <SelectTrigger className="h-8 text-sm w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_FIELDS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {t(`documentImport.rules.fields.${f}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={condition.operator}
                    onValueChange={(v) => updateCondition(i, 'operator', v)}
                  >
                    <SelectTrigger className="h-8 text-sm w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(OPERATORS_BY_FIELD[condition.field] || ['equals']).map((op) => (
                        <SelectItem key={op} value={op}>
                          {t(`documentImport.rules.operators.${op}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    size="sm"
                    className="flex-1"
                    value={String(condition.value)}
                    onChange={(e) => updateCondition(i, 'value', e.target.value)}
                    placeholder={t('documentImport.rules.valuePlaceholder')}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => removeCondition(i)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <AddLineButton label={t('common:app.addLine')} onClick={addCondition} />
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <Label>{t('documentImport.rules.action')}</Label>
          <p className="text-xs text-muted-foreground">
            {t('documentImport.rules.actionsHint')}
          </p>

          {/* A pre-Phase-5a rule whose vendor-linking action was retired. Without
              this the user sees a Save button that is disabled (or an action
              silently missing) with no explanation. */}
          {hasRetiredAction && (
            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
              {t('documentImport.rules.retiredCompanyAction')}
            </div>
          )}

          {/* UUID-targeted actions: read-only (set via correction workflow) */}
          {(actions.set_payment_account_id
            || actions.set_account_id || actions.set_tax_rate_id) && (
            <div className="rounded-md border border-border p-3 bg-muted/30 space-y-1.5 text-xs">
              {actions.set_payment_account_id && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t('documentImport.rules.actionSetPaymentAccount')}</span>
                  <code className="truncate max-w-[280px]">{actions.set_payment_account_id}</code>
                </div>
              )}
              {actions.set_account_id && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t('documentImport.rules.actionSetAccount')}</span>
                  <code className="truncate max-w-[280px]">{actions.set_account_id}</code>
                </div>
              )}
              {actions.set_tax_rate_id && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t('documentImport.rules.actionSetTaxRate')}</span>
                  <code className="truncate max-w-[280px]">{actions.set_tax_rate_id}</code>
                </div>
              )}
              <p className="pt-1 text-[10px] text-muted-foreground italic">
                {t('documentImport.rules.uuidActionsReadOnly')}
              </p>
            </div>
          )}

          {/* Enum-targeted actions: editable */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">
                {t('documentImport.rules.actionSetDocumentType')}
              </Label>
              <Select
                value={actions.set_document_type ?? '__none__'}
                onValueChange={(v) =>
                  setActions({ ...actions, set_document_type: v === '__none__' ? undefined : (v as DocumentType) })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {DOCUMENT_TYPE_VALUES.map((dt) => (
                    <SelectItem key={dt} value={dt}>
                      {t(`documentImport.types.${dt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">
                {t('documentImport.rules.actionSetJeDetail')}
              </Label>
              <Select
                value={actions.set_je_detail_level ?? '__none__'}
                onValueChange={(v) =>
                  setActions({ ...actions, set_je_detail_level: v === '__none__' ? undefined : (v as JeDetailLevel) })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {JE_DETAIL_LEVELS.map((jdl) => (
                    <SelectItem key={jdl} value={jdl}>
                      {t(`documentImport.jeDetailLevels.${jdl}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Priority + toggles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="rule-priority" className="text-xs">
              {t('documentImport.rules.priority')}
            </Label>
            <Input
              id="rule-priority"
              type="number"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)}
              min={0}
              max={1000}
              className="h-8 text-xs"
            />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border px-3">
            <Label htmlFor="rule-auto-approve" className="text-xs cursor-pointer">
              {t('documentImport.rules.autoApprove')}
            </Label>
            <Switch
              id="rule-auto-approve"
              checked={autoApprove}
              onCheckedChange={setAutoApprove}
            />
          </div>
          {isEditing && (
            <div className="flex items-center justify-between rounded-md border border-border px-3">
              <Label htmlFor="rule-is-active" className="text-xs cursor-pointer">
                {t('documentImport.rules.active')}
              </Label>
              <Switch
                id="rule-is-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          )}
        </div>
      </div>
    </DialogForm>
  );
}
